import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { track } from "@/lib/analytics";

const HERO_IMG =
  "https://images.unsplash.com/photo-1587094313669-faf7668ed8a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxhdXN0cmFsaWFuJTIwaG9tZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NjE0NjU2Mnww&ixlib=rb-4.1.0&q=85";

export default function Hero({ onCtaClick }) {
  return (
    <section id="top" className="relative border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 lg:pr-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-6"
              data-testid="hero-eyebrow"
            >
              <ShieldCheck size={14} className="text-primary" />
              Insured · Vetted tradies · Fixed pricing
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-bold tracking-tight text-secondary"
              data-testid="hero-title"
            >
              Snap.<br />
              Send.<br />
              <span className="text-primary">Repair.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed"
              data-testid="hero-sub"
            >
              Minor home repairs and end-of-lease make-goods, sorted from your phone. Snap a few photos, send them over, and a vetted local tradie will fix it — insured, upfront, no fuss.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => { track("hero_cta_click", { location: "hero_primary" }); onCtaClick(); }}
                className="btn-lift bg-primary text-primary-foreground rounded-sm px-6 py-3.5 text-base font-medium inline-flex items-center gap-2"
                data-testid="hero-cta"
              >
                Get my fixed-price quote
                <ArrowRight size={18} />
              </button>
              <a
                href="#how"
                className="text-sm font-medium text-secondary underline underline-offset-4 hover:text-primary transition-colors"
                data-testid="hero-how-link"
              >
                See how it works
              </a>
            </motion.div>

            <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <div>
                <div className="font-display text-2xl font-bold text-secondary">24hr</div>
                <div>Response guarantee</div>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <div className="font-display text-2xl font-bold text-secondary">$20M</div>
                <div>Public liability</div>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <div className="font-display text-2xl font-bold text-secondary">4.9★</div>
                <div>Customer rating</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-sm overflow-hidden aspect-[4/5] grain-overlay"
              data-testid="hero-image"
            >
              <img
                src={HERO_IMG}
                alt="Australian home exterior"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <div className="text-xs uppercase tracking-widest opacity-80">Job #2418</div>
                <div className="font-display text-lg font-semibold mt-1">Bathroom tile crack · Newtown NSW</div>
                <div className="text-xs opacity-80 mt-1">Quoted in 47 min · Repaired next day</div>
              </div>
            </motion.div>
            <div
              className="hidden lg:block absolute -left-10 -bottom-10 bg-secondary text-secondary-foreground rounded-sm px-5 py-4 shadow-xl"
              data-testid="hero-badge"
            >
              <div className="font-display text-3xl font-bold leading-none">$0</div>
              <div className="text-xs opacity-70 mt-1">Quote fee · always</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
